import { useQueryClient } from '@tanstack/react-query';
import { RefObject, useEffect } from 'react';

import { detectPreviewSupportIntent, forwardableMessage } from './previewSupportIntent';
import { Endpoint } from '@/Config/api/endpoint';
import { User } from '@/Domain/api/User/dto/User.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useSupportAiTrigger } from '@/Domain/hooks/App/useSupportAiTrigger.hook';
import { getCurrentScenarioId } from '@/context';

const TRIGGER = 'ai_preview_support_intent' as const;

const WIDGET_URL = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';
const WIDGET_ORIGIN = new URL(WIDGET_URL).origin;

/** Hands a preview send that is really aimed at Quickchat Support to Support AI, observing only. */
export const usePreviewSupportIntent = (
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
) => {
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const { triggerSupportAi, cancelSupportAiTrigger } = useSupportAiTrigger();

  // Its own effect, not the listener's cleanup: that one re-runs on every identity change, and
  // cancelling there would drop a live trigger on an unrelated re-render.
  useEffect(() => {
    if (!enabled) cancelSupportAiTrigger();
  }, [enabled, cancelSupportAiTrigger]);

  useEffect(() => {
    if (!enabled) return;

    const handleSubmit = (event: MessageEvent) => {
      // Read at send time: gating the listener on it would miss a `myself` that resolves after mount.
      if (!queryClient.getQueryData<User>([Endpoint.user.myself])) return;
      // Load-bearing: the Support AI panel is an iframe of the same widget app, so the message we
      // inject into it echoes back as a quickchat_submit that matches by construction.
      if (event.origin !== WIDGET_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type !== 'quickchat_submit') return;

      const message = event.data?.data?.message;
      if (typeof message !== 'string') return;

      const rule = detectPreviewSupportIntent(message);
      if (!rule) return;

      capture(PostHogEventEnum.PreviewSupportIntentDetected, {
        // SPA-aware, unlike the analytics hook's module-level scenario id.
        scenario_id: getCurrentScenarioId(),
        rule,
        message_length: message.length,
      });
      triggerSupportAi({
        trigger: TRIGGER,
        message: forwardableMessage(message),
        properties: { scenario_id: getCurrentScenarioId(), rule },
      });
    };

    window.addEventListener('message', handleSubmit);
    return () => window.removeEventListener('message', handleSubmit);
  }, [enabled, queryClient, iframeRef, capture, triggerSupportAi]);
};
